import React, { createContext, MutableRefObject, ReactElement, useEffect, useRef, useState } from 'react'

/**
 * Modal action prompt context type.
 */
export type ModalExitPromptType = {
    closeExitPrompt: () => void
    displayExitPrompt: () => void
    isExitPromptDisplayed: boolean
}

/**
 * Modal action prompt provider props type.
 */
type ModalExitPromptProviderProps = {
    children: React.ReactNode
}

/**
 * Provides access to prompt resource. Global state handling flag depicting whether the close prompt should be rendered.
 * This context does not render the prompt itself.
 * @param children Children to render.
 */
export const ModalExitPromptContextProvider = ({ children }: ModalExitPromptProviderProps): ReactElement => {
    const [isExitPromptDisplayed, setIsExitPromptDisplayed] = useState<boolean>(false)

    /**
     * Prevents setState after this provider unmounts (e.g. deferred `closeExitPrompt` from `Dialog`'s
     * delayed reset after confirm).
     */
    const isMountedRef: MutableRefObject<boolean> = useRef<boolean>(true)

    useEffect((): (() => void) => {
        isMountedRef.current = true

        return (): void => {
            isMountedRef.current = false
        }
    }, [])

    const displayExitPrompt = (): void => {
        if (!isMountedRef.current) return

        setIsExitPromptDisplayed(true)
    }

    const closeExitPrompt = (): void => {
        if (!isMountedRef.current) return

        setIsExitPromptDisplayed(false)
    }

    return (
        <ModalExitPromptContext.Provider
            value={{
                displayExitPrompt,
                isExitPromptDisplayed,
                closeExitPrompt
            }}
        >
            {children}
        </ModalExitPromptContext.Provider>
    )
}

/**
 * Context for prompt resource.
 */
const defaultModalExitPromptContext: ModalExitPromptType = {
    closeExitPrompt: (): void => undefined,
    displayExitPrompt: (): void => undefined,
    isExitPromptDisplayed: false
}

const ModalExitPromptContext = createContext<ModalExitPromptType>(defaultModalExitPromptContext)

export default ModalExitPromptContext
